import { useCallback, useEffect, useState } from 'react'
import { StatsBar } from './components/StatsBar'
import { AlertsChart } from './components/AlertsChart'
import { ActionsChart } from './components/ActionsChart'
import { ReportsTable } from './components/ReportsTable'
import { DiscoveryPanel } from './components/DiscoveryPanel'
import { RulesMatrix } from './components/RulesMatrix'
import { QueueStats } from './components/QueueStats'
import { ReportDetail } from './components/ReportDetail'
import { BrandsManager } from './components/BrandsManager'
import { MerchantsManager } from './components/MerchantsManager'
import { WebScanPanel } from './components/WebScanPanel'
import { LoginPage } from './components/LoginPage'
import { UsersManager } from './components/UsersManager'
import { OrgManager } from './components/OrgManager'
import { ErrorState, errorMessage } from './components/ErrorState'
import { GuidePage } from './components/GuidePage'
import { ErrorBoundary } from './components/ErrorBoundary'
import { BettingModule } from './components/betting/BettingModule'
import { AdsModule } from './modules/ads/AdsModule'
import { AdsProvider } from './modules/ads/AdsContext'
import { useAuth } from './AuthContext'
import { api } from './api'
import type { DashboardStats } from './types'
import { AppShell } from './shell/AppShell'
import type { ScreenScopes } from './shell/AppShell'
import { ShellProvider, useShell } from './shell/ShellContext'
import { canAccess, findItem, visibleNavigation } from './shell/navigation'
import { navigate, useRoute } from './shell/router'
import { useT } from './i18n'
import type { TFunction } from './i18n'
import { PageHeader } from './ui/ui'

export default function App() {
  const { user, loading } = useAuth()
  const t = useT()

  if (loading) {
    return <div className="login-page"><p>{t('common.loading')}</p></div>
  }
  if (!user) return <LoginPage />

  return (
    <ShellProvider>
      <AuthenticatedApp />
    </ShellProvider>
  )
}

/** Pays et période utilisés par chaque écran, pour n'afficher que les sélecteurs utiles. */
function scopesFor(path: string[]): ScreenScopes {
  switch (path[0]) {
    case 'overview':
      return { period: true }
    case 'ads':
      return { country: true }
    case 'betting':
      // Un sélecteur sans effet sur l'écran induirait en erreur : on ne montre que ceux qui filtrent.
      if (path[1] === 'referentials' || (path[1] === 'tests' && path[2])) return {}
      if (path[1] === 'services' || path[1] === 'qualify') return { country: true }
      return { country: true, period: true }
    default:
      return {}
  }
}

function AuthenticatedApp() {
  const { user } = useAuth()
  const route = useRoute()
  const item = findItem(route)
  const allowed = item ? canAccess(user, item) : false

  // Route inconnue ou interdite : premier écran autorisé.
  useEffect(() => {
    if (item && allowed) return
    const first = visibleNavigation(user)[0]?.items[0]
    if (first) navigate(first.path)
  }, [item, allowed, user])

  // Détection pubs : les scans en cours vivent dans un contexte monté dès la
  // première visite, pour survivre à la navigation vers un autre écran.
  const adsItem = findItem(['ads'])
  const adsAllowed = adsItem ? canAccess(user, adsItem) : false
  const [adsEverOpened, setAdsEverOpened] = useState(false)
  useEffect(() => {
    if (route[0] === 'ads') setAdsEverOpened(true)
  }, [route])

  const content = item && allowed ? <Screen path={route} /> : null
  const shell = (
    <AppShell path={route} scopes={scopesFor(route)}>
      {content}
    </AppShell>
  )

  return <AdsProvider enabled={adsAllowed && adsEverOpened}>{shell}</AdsProvider>
}

function Screen({ path }: { path: string[] }) {
  const t = useT()
  const [head, ...rest] = path

  switch (head) {
    case 'overview':
      return (
        <ErrorBoundary label={t('screen.overview.title')}>
          <OverviewScreen />
        </ErrorBoundary>
      )
    case 'merchants':
      if (rest[0] === 'reports' && rest[1]) {
        return (
          <ErrorBoundary label={t('screen.reportDetail.title')}>
            <ReportDetail reportId={rest[1]} onBack={() => navigate('merchants/reports')} />
          </ErrorBoundary>
        )
      }
      if (rest[0] === 'reports') {
        return (
          <ErrorBoundary label={t('screen.reports.title')}>
            <PageHeader title={t('screen.reports.title')} description={t('screen.reports.desc')} />
            <ReportsTable onSelectReport={(id) => navigate(`merchants/reports/${id}`)} />
            <QueueStats />
          </ErrorBoundary>
        )
      }
      return (
        <ErrorBoundary label={t('screen.merchants.title')}>
          <PageHeader title={t('screen.merchants.title')} description={t('screen.merchants.desc')} />
          <MerchantsManager />
        </ErrorBoundary>
      )
    case 'discovery':
      return (
        <ErrorBoundary label={t('screen.discovery.title')}>
          <PageHeader title={t('screen.discovery.title')} description={t('screen.discovery.desc')} />
          <DiscoveryPanel />
        </ErrorBoundary>
      )
    case 'webscan':
      return (
        <ErrorBoundary label={t('screen.webscan.title')}>
          <PageHeader title={t('screen.webscan.title')} description={t('screen.webscan.desc')} />
          <WebScanPanel />
        </ErrorBoundary>
      )
    case 'ads':
      return (
        <ErrorBoundary label={t('screen.ads.title')}>
          <AdsModule view={rest[0] ?? 'overview'} />
        </ErrorBoundary>
      )
    case 'betting':
      return (
        <ErrorBoundary label={t('screen.betting.title')}>
          <BettingModule path={rest} />
        </ErrorBoundary>
      )
    case 'brands':
      return (
        <ErrorBoundary label={t('screen.brands.title')}>
          <PageHeader title={t('screen.brands.title')} description={t('screen.brands.desc')} />
          <BrandsManager />
        </ErrorBoundary>
      )
    case 'rules':
      return (
        <ErrorBoundary label={t('screen.rules.title')}>
          <PageHeader title={t('screen.rules.title')} description={t('screen.rules.desc')} />
          <RulesMatrix />
        </ErrorBoundary>
      )
    case 'users':
      return (
        <ErrorBoundary label={t('screen.users.title')}>
          <PageHeader title={t('screen.users.title')} description={t('screen.users.desc')} />
          <UsersManager />
        </ErrorBoundary>
      )
    case 'orgs':
      return (
        <ErrorBoundary label={t('screen.orgs.title')}>
          <PageHeader title={t('screen.orgs.title')} description={t('screen.orgs.desc')} />
          <OrgManager />
        </ErrorBoundary>
      )
    case 'guide':
      return (
        <ErrorBoundary label={t('screen.guide.title')}>
          <GuidePage />
        </ErrorBoundary>
      )
    default:
      return null
  }
}

function OverviewScreen() {
  const t: TFunction = useT()
  const { period } = useShell()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      setStats(await api.getStats(period))
      setError(null)
    } catch (err) {
      // Outil de conformité : jamais de données inventées, on signale l'échec.
      setStats(null)
      setError(errorMessage(err))
    }
  }, [period])

  useEffect(() => {
    setLoading(true)
    refresh().finally(() => setLoading(false))
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, 30_000)
    return () => clearInterval(timer)
  }, [refresh])

  return (
    <>
      <PageHeader title={t('screen.overview.title')} description={t('screen.overview.desc')} />
      {error && !loading ? (
        <ErrorState message={error} onRetry={refresh} />
      ) : (
        <StatsBar
          stats={stats}
          loading={loading}
          onNavigate={(target) => {
            // Un chiffre « à traiter » mène à la liste correspondante.
            if (target === 'webscan') navigate('webscan')
            else if (target === 'discovery') navigate('discovery')
            else navigate('merchants/reports')
          }}
        />
      )}
      <div className="charts-row">
        <AlertsChart days={period} />
        <ActionsChart hours={24} />
      </div>
      <QueueStats />
    </>
  )
}
